/**
 * AdUnit.js — Google AdSense display unit for the visualizer.
 *
 * Renders a responsive <ins class="adsbygoogle"> unit and lazy-injects the
 * AdSense script the first time an ad is shown. The publisher client id comes
 * from the backend (config.ads.client — set GOOGLE_ADS_CLIENT in .env); the
 * slot is per-placement (save / load).
 *
 * AdSense selects the creative; this component does not guarantee video or revenue.
 *
 * Silent by design: if ads are blocked or the client id is a placeholder,
 * the unit renders nothing and the action continues — an ad must never block
 * the user's work.
 */
import { useEffect } from 'react';

let scriptInjected = false;

function ensureAdsScript(client) {
  if (scriptInjected) return;
  scriptInjected = true;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(client)}`;
  s.crossOrigin = 'anonymous';
  document.head.appendChild(s);
}

export default function AdUnit({ client, slot, format = 'auto', fixedHeight }) {
  useEffect(() => {
    if (!client || window.kickvizAppPrivacy) return;
    ensureAdsScript(client);
    // Push AFTER the <ins> is mounted so AdSense can fill it. Deferred so
    // the script tag has time to load on the first paint.
    const t = setTimeout(() => {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (e) { /* ad blocked — continue silently */ }
    }, 120);
    return () => clearTimeout(t);
  }, [client, slot]);

  if (!client || window.kickvizAppPrivacy) return null;

  return (
    <div style={{
      minHeight: fixedHeight || 'min(140px, 24vh)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(15,23,42,0.55)', borderRadius: '8px',
      border: '1px dashed #334155', overflow: 'hidden', margin: '0.25rem 0 0.75rem',
    }}>
      <ins
        className="adsbygoogle"
        style={{ display: 'block', width: '100%', height: fixedHeight }}
        data-ad-client={client}
        data-ad-slot={slot || undefined}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </div>
  );
}
